import React from 'react';
import { LinearProgress, Theme } from '@mui/material';
import { BrandColors } from '../../constants';
import { getPasswordStrength } from '../utils/validation';
import { StrengthMeter } from '../interfaces/AuthInputFields';
import { useTranslation } from 'react-i18next';
import { PasswordStrengthLabel } from '../constants/passwordStrengthLabel';
import { visuallyHidden } from '@mui/utils';
import { makeStyles } from 'tss-react/mui';

/** Method to get color code by strength */
const getColorByPasswordStrength = (theme: Theme, strength: StrengthMeter): string => {
  let strengthColor: string = theme.palette.passwordStrengthMeter?.weak.color || BrandColors.grey77;
  const { variations, charCount, score } = strength;

  if (score <= 0) {
    strengthColor = theme.palette.passwordStrengthMeter?.weak.color || BrandColors.grey77;
  }
  if (
    !variations.digits ||
    !variations.lower ||
    !variations.upper ||
    charCount <= 8 ||
    charCount >= 50 ||
    !variations.moreThanTwoConsecutiveCharacters
  ) {
    strengthColor = theme.palette.passwordStrengthMeter?.medium.color || BrandColors.red39;
  }
  if (
    variations.digits &&
    variations.lower &&
    variations.upper &&
    charCount >= 8 &&
    charCount < 50 &&
    variations.moreThanTwoConsecutiveCharacters
  ) {
    strengthColor = theme.palette.passwordStrengthMeter?.strong.color || BrandColors.green20;
  }
  return strengthColor;
};

/** Style override to set bar color by strength */
const useStyles = makeStyles<StrengthMeter>()((theme: Theme, strength) => ({
  root: {
    backgroundColor: `${theme.palette.passwordStrengthMeter?.weak.color} !important`,
    borderRadius: '50px',
    height: '0.75rem',
    border: theme.palette.passwordStrengthMeter?.border,
  },
  barColorPrimary: {
    backgroundColor: getColorByPasswordStrength(theme, strength),
  },
}));

/** Type for password strength meter component  */
type PasswordStrengthMeterProps = {
  password: string;
};

/** Component for password strength meter with color as per strength */
export const PasswordStrengthMeter = ({ password }: PasswordStrengthMeterProps): React.JSX.Element => {
  const passwordStrength: StrengthMeter = getPasswordStrength(password);
  const { classes } = useStyles(passwordStrength);
  const { t } = useTranslation();

  const getPasswordStrengthAriaLabel = (score: number): string => {
    let passwordStrengthLabel = '';
    switch (true) {
      case score === 0:
        passwordStrengthLabel = '';
        break;
      case score < 50:
        passwordStrengthLabel = PasswordStrengthLabel.VERY_WEAK;
        break;
      case score < 70:
        passwordStrengthLabel = PasswordStrengthLabel.WEAK;
        break;
      case score < 100:
        passwordStrengthLabel = PasswordStrengthLabel.GOOD;
        break;
      default:
        passwordStrengthLabel = PasswordStrengthLabel.STRONG;
    }

    return t(`${passwordStrengthLabel}`) + t('passwordStrengthIndicator');
  };

  return (
    <>
      {password.length > 0 && (
        <span style={visuallyHidden} role={password.length ? 'alert' : ''}>
          {getPasswordStrengthAriaLabel(passwordStrength.score)}
        </span>
      )}
      <LinearProgress
        variant="determinate"
        aria-label={t('passwordStrengthIndicator')}
        value={passwordStrength.score}
        classes={classes}
      />
    </>
  );
};
